/**
 * The Home tab's split view: Counter and Officer panes side by side (or stacked),
 * with a draggable divider. Pulling the divider well past its limit hides the
 * squeezed pane and switches to the other one.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import { registerViewState, reloadedView } from "@/utils/reloadState";

// Split-pane ratio (first pane's share) limits, and how far past them a drag must go
// — as a fraction of the container — before releasing hides the squeezed pane.
const SPLIT_MIN = 0.33;
const SPLIT_MAX = 0.67;
const SPLIT_HIDE_OVERSHOOT = 0.1;

interface SavedSplit { enabled: boolean; direction: 'horizontal' | 'vertical'; h: number; v: number }

interface SplitPaneOptions {
  /** Whether the layout may split at all (not on a phone, and a roster exists). */
  canSplit: boolean;
  activeView: 'counter' | 'officers';
  onSwitchTab: (tab: string) => void;
}

export function useSplitPane({ canSplit, activeView, onSwitchTab }: SplitPaneOptions) {
  // After an app-update reload the layout comes back as it was left.
  const saved = reloadedView<SavedSplit>('split');
  const [splitEnabled, setSplitEnabled] = useState(saved?.enabled ?? true);
  const [splitDirection, setSplitDirection] = useState<'horizontal' | 'vertical'>(saved?.direction ?? 'horizontal');
  const [splitRatio, setSplitRatio] = useState(saved ? (saved.direction === 'vertical' ? saved.v : saved.h) : 0.56);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartRef = useRef<{ pos: number; ratio: number }>({ pos: 0, ratio: 0.56 });
  // Refs so drag handlers never go stale
  const splitDirectionRef = useRef(splitDirection);
  splitDirectionRef.current = splitDirection;
  const splitRatioRef = useRef(splitRatio);
  splitRatioRef.current = splitRatio;
  const splitEnabledRef = useRef(splitEnabled);
  splitEnabledRef.current = splitEnabled;
  // Remember split ratio per direction — horizontal defaults 56:44, vertical defaults 50:50
  const hRatioRef = useRef(saved?.h ?? 0.56);
  const vRatioRef = useRef(saved?.v ?? 0.5);
  useEffect(() => registerViewState('split', (): SavedSplit => ({
    enabled: splitEnabledRef.current, direction: splitDirectionRef.current, h: hRatioRef.current, v: vRatioRef.current,
  })), []);
  // Coalesces divider-drag ratio updates to one React state set per animation frame.
  // mousemove/touchmove can fire far faster than the display refreshes, and each
  // setSplitRatio call cascades into a full ResizeObserver-driven canvas resize+
  // repaint on both panes — calling it straight from the move handler turned a
  // divider drag into a string of redundant full redraws with nothing new to show
  // between them.
  const splitRatioRafRef = useRef<number | null>(null);
  const pendingSplitRatioRef = useRef<number | null>(null);
  const scheduleSplitRatio = useCallback((ratio: number) => {
    pendingSplitRatioRef.current = ratio;
    if (splitRatioRafRef.current !== null) return;
    splitRatioRafRef.current = requestAnimationFrame(() => {
      splitRatioRafRef.current = null;
      if (pendingSplitRatioRef.current !== null) setSplitRatio(pendingSplitRatioRef.current);
    });
  }, []);
  useEffect(() => () => {
    if (splitRatioRafRef.current !== null) cancelAnimationFrame(splitRatioRafRef.current);
  }, []);
  // Pulling the divider well past its resize limit hides the pane that's being squeezed
  // (the other one then fills the screen), so there's no need to hunt for a separate
  // maximise control. The pull has to overshoot the limit by SPLIT_HIDE_OVERSHOOT of the
  // container and it only commits on release — dragging to the minimum itself, or
  // brushing past it, just stops at the minimum. While a release would hide a pane, that
  // pane is dimmed with a "release to hide" label so the outcome is never a surprise.
  const [hideHint, setHideHint] = useState<'counter' | 'officers' | null>(null);
  const hideHintRef = useRef<'counter' | 'officers' | null>(null);
  const onSwitchTabRef = useRef(onSwitchTab);
  onSwitchTabRef.current = onSwitchTab;
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;
  const dragSplitTo = useCallback((rawRatio: number) => {
    scheduleSplitRatio(Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, rawRatio)));
    const hint = rawRatio < SPLIT_MIN - SPLIT_HIDE_OVERSHOOT ? 'counter'
      : rawRatio > SPLIT_MAX + SPLIT_HIDE_OVERSHOOT ? 'officers' : null;
    if (hint !== hideHintRef.current) {
      hideHintRef.current = hint;
      setHideHint(hint);
    }
  }, [scheduleSplitRatio]);
  const endSplitDrag = useCallback(() => {
    const hidden = hideHintRef.current;
    if (!hidden) return;
    hideHintRef.current = null;
    setHideHint(null);
    setSplitEnabled(false);
    const keep = hidden === 'counter' ? 'officers' : 'counter';
    if (activeViewRef.current !== keep) onSwitchTabRef.current?.(keep);
  }, []);
  useEffect(() => {
    setSplitRatio(splitDirection === 'horizontal' ? hRatioRef.current : vRatioRef.current);
  }, [splitDirection]);
  useEffect(() => {
    if (splitDirection === 'horizontal') hRatioRef.current = splitRatio;
    else vRatioRef.current = splitRatio;
  }, [splitRatio, splitDirection]);

  // Collapse split when user deliberately switches Counter ↔ Officers tab
  const prevActiveView = useRef(activeView);
  useEffect(() => {
    if (prevActiveView.current === activeView) return;
    prevActiveView.current = activeView;
    setSplitEnabled(false);
  }, [activeView]);

  // Stable mouse-drag handler (refs-only, no stale closure issues)
  const onDividerMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDragging.current = true;
    dividerRef.current?.setAttribute('data-active', '');
    const dir = splitDirectionRef.current;
    dragStartRef.current = {
      pos: dir === 'horizontal' ? e.clientX : e.clientY,
      ratio: splitRatioRef.current,
    };
    const onMove = (me: MouseEvent) => {
      if (!isDragging.current || !splitContainerRef.current) return;
      const d = splitDirectionRef.current;
      const size = d === 'horizontal' ? splitContainerRef.current.offsetWidth : splitContainerRef.current.offsetHeight;
      const delta = (d === 'horizontal' ? me.clientX : me.clientY) - dragStartRef.current.pos;
      dragSplitTo(dragStartRef.current.ratio + delta / size);
    };
    const onUp = () => {
      isDragging.current = false;
      dividerRef.current?.removeAttribute('data-active');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      endSplitDrag();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [dragSplitTo, endSplitDrag]); // both are stable ([] deps / refs only)


  // In split-screen mode (tablet/desktop), both grids show side by side.
  const isSplit = canSplit && splitEnabled;
  // Read synchronously by the touch handler below.
  const isSplitRef = useRef(false);
  isSplitRef.current = isSplit;

  // Non-passive touch listener attached directly to the divider element.
  // React onTouchStart is passive by default in modern React, which prevents
  // preventDefault() from stopping iOS scroll, breaking the drag.
  // Declared here (after isSplit) so [isSplit] can be used in deps without TDZ error.
  useEffect(() => {
    const el = dividerRef.current;
    if (!el) return;
    const handler = (e: TouchEvent) => {
      if (!isSplitRef.current) return;
      e.preventDefault(); // stop iOS scroll from hijacking drag
      const touch = e.touches[0];
      isDragging.current = true;
      el.setAttribute('data-active', '');
      const dir = splitDirectionRef.current;
      dragStartRef.current = {
        pos: dir === 'horizontal' ? touch.clientX : touch.clientY,
        ratio: splitRatioRef.current,
      };
      const onMove = (te: TouchEvent) => {
        te.preventDefault();
        if (!isDragging.current || !splitContainerRef.current) return;
        const t = te.touches[0];
        const d = splitDirectionRef.current;
        const size = d === 'horizontal' ? splitContainerRef.current.offsetWidth : splitContainerRef.current.offsetHeight;
        const delta = (d === 'horizontal' ? t.clientX : t.clientY) - dragStartRef.current.pos;
        dragSplitTo(dragStartRef.current.ratio + delta / size);
      };
      const onEnd = () => {
        isDragging.current = false;
        el.removeAttribute('data-active');
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onEnd);
        window.removeEventListener('touchcancel', onEnd);
        endSplitDrag();
      };
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onEnd);
      window.addEventListener('touchcancel', onEnd);
    };
    el.addEventListener('touchstart', handler, { passive: false });
    return () => el.removeEventListener('touchstart', handler);
  // Re-attach when split activates so we catch the case where divider wasn't mounted on first run.
  // dragSplitTo/endSplitDrag are stable, so listing them doesn't cause re-attachment.
  }, [isSplit, dragSplitTo, endSplitDrag]);

  return {
    isSplit,
    splitDirection,
    setSplitDirection,
    splitRatio,
    setSplitEnabled,
    splitContainerRef,
    dividerRef,
    hideHint,
    onDividerMouseDown,
  };
}
